import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { PassportService } from '../../services/passport.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { Stamp } from '../../models/profile.model';
import { Constants } from 'src/app/core/models/common.models';
import { ActivatedRoute } from '@angular/router';
import { EventType } from '../../models/passport.models';
import { environment } from 'src/environments/environment';

const SHOW_ALL_SEASONS = '--Select Year--';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit {

  private static readonly EVENTS_IMAGE_PREFIX = environment.eventImageBaseURL;
  private static readonly ASSETS_BASE_URL = environment.passportAssetsBaseURL;

  @ViewChild('seasonSelect') seasonSelect: ElementRef;

  spotlightsData: any;
  spotlightYear = SHOW_ALL_SEASONS;
  spotlightYearIndex = 1;
  mySelect = '2021';
  eventLength: any;

  private userId: number;

  public profile: any = null;
  public spotlights: Array<Stamp> = [];
  public showcases: Array<any> = [];
  public screeningparties: Array<any> = [];
  public favouriteEvents: any = [];

  public eventTypes = {
    spotlight: EventType.SPOTLIGHT,
    showcase: EventType.SHOWCASE,
    screeningParty: EventType.SCREENING_PARTY
  };

  public form: FormGroup;

  public readonly = false;
  public isEditMode = false;
  public dataState: Promise<boolean> = Promise.resolve(false);
  public userPurchaseHistory: any = {};

  constructor(private passportService: PassportService,
    private route: ActivatedRoute,
    private ngxSpinner: NgxSpinnerService) { }

  ngOnInit(): void {
    this.ngxSpinner.show();
    this.route.data.subscribe(data => {
      this.readonly = data && data.readonly;
    });
    this.route.params.subscribe(params => {
      if (params.userId) {
        this.userId = Number(params.userId);
        this.initializeCommunityProfile(params.userId);
      } else {
        this.initializeProfile();
      }
    });
  }

  private initializeProfile(refreshOnlyPurchaseHistory?: boolean): void {
    this.ngxSpinner.show();
    Promise.all([this.passportService.getData(), this.passportService.getSeasonsFilters()])
      .then(([profile, seasonsFilters]) => {
        this.profile = profile;
        localStorage.setItem(Constants.localStorageKeys.PASSPORT_PROFILE, JSON.stringify(profile));

        Promise.all([this.getEventPurchaseHistory(true)])
          .then(() => {
            if (refreshOnlyPurchaseHistory) {
              this.showSeasonEvents(this.spotlightYear);
            } else {
              this.spotlightsData = seasonsFilters;
              this.initializeStampsWithLatestSeasonSelected();
            }

            this.ngxSpinner.hide();
          }).catch((error) => {
            console.error(error);
            this.ngxSpinner.hide();
          });

        this.dataState = Promise.resolve(true);
      })
      .catch(error => {
        console.error(error);
        this.dataState = Promise.resolve(false);
        this.ngxSpinner.hide();
      });
  }

  private initializeCommunityProfile(userId: number): void {
    this.ngxSpinner.show();
    Promise.all([this.passportService.getCommunityPassport(userId), this.passportService.getSeasonsFilters()])
      .then(([profile, seasonsFilters]) => {
        this.profile = profile;

        Promise.all([this.getEventPurchaseHistory()])
          .then(() => {
            this.spotlightsData = seasonsFilters;

            this.initializeStampsWithLatestSeasonSelected();

            this.ngxSpinner.hide();
          }).catch((error) => {
            console.error(error);
            this.ngxSpinner.hide();
          });

        this.dataState = Promise.resolve(true);
      })
      .catch(error => {
        console.error('Failed to initialize community passport', error);
        this.dataState = Promise.resolve(false);
        this.ngxSpinner.hide();
      });
  }

  private initializeStampsWithLatestSeasonSelected(): void {
    if (this.spotlightsData && this.spotlightsData.length) {
      const firstSeason = `${this.spotlightsData[0].season} ${this.spotlightsData[0].year}`;

      console.log('setting season selector to: ---' + firstSeason);
      this.spotlightYear = firstSeason;
      this.spotlightYearIndex = 1;

      this.showSeasonEvents(firstSeason);
    }
  }

  editProfile(event: any): void {
    this.isEditMode = event.value === 'edit-profile';
    if (event.profile) {
      this.initializeProfile();
    }
  }

  async getEventPurchaseHistory(forceRefresh = false): Promise<any> {
    if (this.readonly) {
      this.userPurchaseHistory = await this.passportService.getCommunityUserEventPurchaseHistory(this.userId);
    } else {
      this.userPurchaseHistory = await this.passportService.getEventPurchaseHistory();
    }

    if (forceRefresh) {
      this.spotlights = [];
      this.showcases = [];
      this.screeningparties = [];
      this.favouriteEvents = [];
    }

    let spotlightIndex = 0;
    this.eventLength = 0;

    this.userPurchaseHistory.spotlightEvents.forEach((spotlightEvent: any) => {
      this.spotlights.push(this.buildSpotlightStamp(spotlightEvent, spotlightIndex));
      spotlightIndex++;
      this.eventLength++;
    });

    let showcaseIndex = 1;
    this.userPurchaseHistory.showcaseMovies.forEach((showcase: any) => {
      this.showcases.push(this.buildShowcaseStamp(showcase, showcaseIndex));
      showcaseIndex++;
      this.eventLength++;
    });

    let eventPartyIndex = 2;
    this.userPurchaseHistory.screeningParties.forEach((screeningParty: any) => {
      this.screeningparties.push(this.buildScreeningPartyStamp(screeningParty, eventPartyIndex));
      eventPartyIndex++;
      this.eventLength++;
    });
  }

  private buildSpotlightStamp(spotlightEvent: any, spotlightIndex: number): Stamp {
    const stamp = {
      name: spotlightEvent.Name,
      image: this.buildImageURL(spotlightEvent, spotlightIndex, 'spotlight'),
      shortDescription: spotlightEvent.ShortDescription,
      eventId: spotlightEvent.EventID,
      eventType: spotlightEvent.eventType,
      eventImage: spotlightEvent.EventImage.includes("https://")?spotlightEvent.EventImage:`${ProfileComponent.EVENTS_IMAGE_PREFIX}/${spotlightEvent.EventImage}`,
      purchaseTime: new Date(spotlightEvent.purchaseTime || null),
      favorite: spotlightEvent.favorite || false,
      eventReviewStatus: spotlightEvent.eventReviewStatus,
      eventIndex: spotlightIndex
    };

    if (spotlightEvent.favorite && this.eventNotAdded(stamp)) {
      this.favouriteEvents.push(stamp);
    }

    return stamp;
  }

  private buildImageURL(cinejoyEvent: any, eventTypeIndex: number, eventType: 'spotlight' | 'screeningparty' | 'showcase'): string {
    let year = '';
    let season = '';

    if (this.spotlightYear !== SHOW_ALL_SEASONS) {
      season = this.spotlightYear.split(' ')[0];
      year = this.spotlightYear.split(' ')[1];
    } else {
      if (eventType === 'screeningparty') {
        year = cinejoyEvent.movieDetails.passportProperties.cinejoyYears[0];
        season = cinejoyEvent.movieDetails.passportProperties.cinejoySeasons[0];
      } else {
        year = cinejoyEvent.passportProperties.cinejoyYears[0];
        season = cinejoyEvent.passportProperties.cinejoySeasons[0];
      }
    }

    const color = this.getColour(eventTypeIndex);

    let imageURL = `assets/images/passport/${eventType}/${color}.png`;

    if (year && season) {
      imageURL = `${ProfileComponent.ASSETS_BASE_URL}/passport/${eventType}/${year}/${season.toLowerCase()}/${color}.png`;
    }
    return imageURL;
  }

  private eventNotAdded(stamp: Stamp): boolean {
    return !this.favouriteEvents.length
      || !this.favouriteEvents.find((event: any) => event.eventId === stamp.eventId);
  }

  private buildShowcaseStamp(showcase: any, showcaseIndex: number): Stamp {
    const stamp = {
      name: showcase.Name,
      image: this.buildImageURL(showcase, showcaseIndex, 'showcase'),
      shortDescription: showcase.ShortDescription,
      eventId: showcase.EventID,
      eventType: showcase.eventType,
      eventImage: showcase.EventImage.includes("https://")?showcase.EventImage:`${ProfileComponent.EVENTS_IMAGE_PREFIX}/${showcase.EventImage}`,
      purchaseTime: new Date(showcase.purchaseTime || null),
      favorite: showcase.favorite || false,
      eventReviewStatus: showcase.eventReviewStatus,
      eventIndex: showcaseIndex
    };

    if (showcase.favorite && this.eventNotAdded(stamp)) {
      this.favouriteEvents.push(stamp);
    }

    return stamp;
  }

  private buildScreeningPartyStamp(screeningParty: any, eventPartyIndex: number): Stamp {
    const stamp = {
      name: screeningParty.movieDetails.Name,
      image: this.buildImageURL(screeningParty, eventPartyIndex, 'screeningparty'),
      shortDescription: screeningParty.movieDetails.ShortDescription,
      eventId: screeningParty.movieDetails.EventID,
      eventType: screeningParty.movieDetails.eventType,
      eventImage: screeningParty.movieDetails.EventImage.includes("https://")?screeningParty.movieDetails.EventImage:`${ProfileComponent.EVENTS_IMAGE_PREFIX}/${screeningParty.movieDetails.EventImage}`,
      purchaseTime: new Date(screeningParty.meetingDateTime || null),
      favorite: screeningParty.favorite || false,
      eventReviewStatus: screeningParty.movieDetails.eventReviewStatus,
      eventIndex: eventPartyIndex
    };

    if (screeningParty.favorite && this.eventNotAdded(stamp)) {
      this.favouriteEvents.push(stamp);
    }

    return stamp;
  }

  getColour(index: number): string {
    if (index % 3 === 0) {
      return 'red';
    }
    if (index % 3 === 1) {
      return 'white';
    }
    if (index % 3 === 2) {
      return 'gold';
    }
    return 'gold';
  }

  selectChange(): void {
    this.spotlights = [];
    this.showcases = [];
    this.screeningparties = [];
    this.eventLength = 0;

    const spotlightYear = this.spotlightYear;

    if (spotlightYear !== SHOW_ALL_SEASONS) {
      this.showSeasonEvents(spotlightYear);
    } else {
      this.getEventPurchaseHistory();
    }
  }

  handleRefreshEventState(state: boolean): void {
    if (state) {
      this.initializeProfile(true);
    }
  }

  private showSeasonEvents(spotlightYear: string): void {
    const searchData = spotlightYear.split(' ');

    this.spotlights = [];
    this.showcases = [];
    this.screeningparties = [];
    this.eventLength = 0;

    let spotlightIndex = 0;
    this.userPurchaseHistory.spotlightEvents.forEach((spotlightEvent: any) => {
      if (spotlightYear === SHOW_ALL_SEASONS
        || (spotlightEvent.passportProperties.cinejoySeasons.some(i => i === searchData[0])
          && spotlightEvent.passportProperties.cinejoyYears.some(i => i === searchData[1]))) {
        this.spotlights.push(this.buildSpotlightStamp(spotlightEvent, spotlightIndex));
        spotlightIndex++;
        this.eventLength++;
      }
    });

    let showcaseIndex = 1;
    this.userPurchaseHistory.showcaseMovies.forEach((showcase: any) => {
      if (spotlightYear === SHOW_ALL_SEASONS
        || (showcase.passportProperties.cinejoySeasons.some(i => i === searchData[0])
          && showcase.passportProperties.cinejoyYears.some(i => i === searchData[1]))) {
        this.showcases.push(this.buildShowcaseStamp(showcase, showcaseIndex));
        showcaseIndex++;
        this.eventLength++;
      }
    });

    let eventPartyIndex = 2;
    this.userPurchaseHistory.screeningParties.forEach((screeningParty: any) => {
      if (spotlightYear === SHOW_ALL_SEASONS
        || (screeningParty.movieDetails.passportProperties.cinejoySeasons.some((i: any) => i === searchData[0])
          && screeningParty.movieDetails.passportProperties.cinejoyYears.some((i: any) => i === searchData[1]))) {
        this.screeningparties.push(this.buildScreeningPartyStamp(screeningParty, eventPartyIndex));
        eventPartyIndex++;
        this.eventLength++;
      }
    });
  }
}
